// ─── CONFIGURATION d'une enveloppe : toutes ses prévisions ────────
// Le 🧾 de l'enveloppe (11/08, demande de Cédric) : « lister les opérations
// prévisionnelles pour la configuration du budget », INDÉPENDAMMENT du mois.
//
// C'est le pendant « réglage » du plan mensuel : là-bas on lit ce qui est dû
// en août ; ici on voit TOUT ce qui a été prévu, avec sa fréquence et sa
// période de validité — donc on peut corriger une ligne qui n'apparaît nulle
// part parce qu'elle est mal configurée.
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran,
  useUI, useT, confirmerSuppression, confirmerChoix,
} from '@mop/core';
import { formatEuro, montantOperation } from './services/calculs';
import { lignesDeBudget, moyenneRelevee } from './services/occurrences';
import { filtrerPlan, TRIS_LIGNES, nbFiltresReleve } from './services/filtres';
import { emojiBudget, emojiCategorie, cleCategorie } from './data/categories';
import FeuilleFiltresCompte from './FeuilleFiltresCompte';
import EnTeteArbre, { RETRAIT_ENFANTS } from './EnTeteArbre';
import BarreRecherche from './BarreRecherche';
import { prefSession, definirPrefSession } from './services/prefsSession';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function EcranConfigBudget({
  budget, lignes = [], onFermer, onNouvelle, onFiche, onEditerLigne, onSupprimerLigne,
  onModeMois,   // retour à la vue MOIS — la bascule doit marcher dans les deux sens
  toutes = false,   // TOUTES les previsions, tous budgets confondus
  onNouveauBudget,  // en vue GLOBALE : creer une enveloppe reste possible
  budgets = [],     // pour NOMMER les groupes en vue globale
  // Gérer les budgets DEPUIS la vue liste (Cédric, 15/08) : tap = fiche,
  // swipe → = éditer, swipe ← = supprimer. La confirmation ET le verrou
  // « pas de suppression tant qu'il reste des lignes » vivent chez le parent.
  onFicheBudget, onEditerBudget, onSupprimerBudget,
  // Le RÉEL descend jusqu'ici (16/08) : la moyenne relevée de chaque ligne
  // s'affiche sous son prévu — c'est le chiffre qui dit s'il tient la route.
  actuals = [], exceptions = [], rattachements = null,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [recherche, setRecherche] = useState('');
  const [sens, setSens] = useState('tout');
  const [tri, setTri] = useState('montant');
  const [feuille, setFeuille] = useState(null);
  // La SYNTHÈSE se plie depuis l'en-tête (16/08) — mémorisé pour la session.
  const [syntheseVisible, setSyntheseVisible] = useState(() => prefSession('syntheseConfig', true));
  const basculerSynthese = () => setSyntheseVisible(v => {
    definirPrefSession('syntheseConfig', !v);
    return !v;
  });
  // Les groupes REPLIÉS, par identifiant de budget (`null` = sans budget).
  // On stocke les repliés et non les ouverts : à la première ouverture tout
  // est visible, ce qui est le comportement attendu.
  // Replies d'emblee (Cedric, 15/08) — voir ArbreBudgetsMois.
  const [replies, setReplies] = useState(null);
  const basculer = (cle) => setReplies((prev) => {
    const n = new Set(prev ?? (groupes || []).map(g => g.cle));
    if (n.has(cle)) n.delete(cle); else n.add(cle);
    return n;
  });

  // `toutes` : la vue globale ne filtre par aucun budget — y compris les
  // previsions orphelines, qui seraient sinon invisibles ici aussi.
  const mesLignes = useMemo(
    () => (toutes ? lignes : lignesDeBudget(lignes, budget?.id)),
    [lignes, budget, toutes],
  );
  // On réutilise le filtre du PLAN en présentant chaque ligne comme une
  // pseudo-occurrence : même code de recherche et de tri, aucune duplication.
  // `prevu` est le montant nominal de la ligne — il n'y a pas de mois ici,
  // donc ni inflation ni échéancier : c'est la configuration, pas une échéance.
  const commeOccurrences = useMemo(
    () => mesLignes.map(l => ({ ligne: l, ligneId: l.id, prevu: Math.abs(Number(l.montant) || 0) })),
    [mesLignes],
  );
  const liste = useMemo(
    () => filtrerPlan(commeOccurrences, { recherche, sens, tri }),
    [commeOccurrences, recherche, sens, tri],
  );
  // Groupes = budgets, dans l’ordre des budgets ; les orphelines en dernier,
  // parce qu'elles sont un reste à ranger, pas une catégorie.
  const groupes = useMemo(() => {
    if (!toutes) return null;
    const par = new Map();
    for (const o of liste) {
      const cle = o.ligne?.budgetId || null;
      if (!par.has(cle)) par.set(cle, []);
      par.get(cle).push(o);
    }
    const ordre = [...budgets.map(b => b.id), null].filter(c => par.has(c));
    return ordre.map(cle => ({
      cle,
      budget: budgets.find(b => b.id === cle) || null,
      lignes: par.get(cle),
      total: par.get(cle).reduce((n, o) => n + Math.abs(o.prevu || 0), 0),
      // Total nominal SIGNÉ pour l'en-tête (15/08) : l'effectif seul ne
      // disait pas ce que pèse l'enveloppe.
      totalSigne: par.get(cle).reduce(
        (n, o) => n + (o.ligne?.sens === 'revenu' ? 1 : -1) * Math.abs(o.prevu || 0), 0),
    }));
  }, [toutes, liste, budgets]);

  const nbFiltres = nbFiltresReleve({ sens });

  // La moyenne relevée de chaque ligne visible, calculée une fois par rendu
  // des données — pas à chaque carte.
  const moyennes = useMemo(() => {
    const m = new Map();
    for (const o of liste) {
      if (o.ligne?.id) m.set(o.ligne.id, moyenneRelevee(o.ligne, { actuals, exceptions, rattachements }));
    }
    return m;
  }, [liste, actuals, exceptions, rattachements]);

  // Période COMPACTE (Cédric, 16/08) : « 09/2025 – 08/2026 », et « ∞ » pour
  // une ligne sans fin — « depuis Septembre 2025 · jusqu'à… » tronquait la
  // carte. Le détail en toutes lettres reste dans la fiche.
  const mmAAAA = (mk) => { const [y, m] = String(mk || '').split('-'); return (y && m) ? `${m}/${y}` : ''; };
  const periode = (l) => (l.debut ? `${mmAAAA(l.debut)} – ${l.fin ? mmAAAA(l.fin) : '∞'}` : '');

  return (
    <GesteRetourEcran actif onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={toutes ? t('finances.ongletBudget') : (budget?.nom || '')}
          iconeTitre={toutes ? '💰' : emojiBudget(budget)} onRetour={onFermer}
          /* En vue GLOBALE, ce ＋ est le SEUL visible : il couvre l'ecran des
             budgets, dont le ＋ creait l'enveloppe. Sans choix, on ne pouvait
             plus creer de budget du tout (Cedric, 14/08). */
          action={{ icone: '＋',
            label: t(toutes ? 'commun.ajouter' : 'finances.nouvelleLigne'),
            onPress: () => {
              if (!toutes || !onNouveauBudget) return onNouvelle?.();
              confirmerChoix(t('commun.ajouter'), '', [
                // ⚠️ La cle est `text` — le contrat de `Alert.alert`, que
                // `confirmerChoix` reprend tel quel. Avec `texte`, les boutons
                // s'affichent VIDES (constate le 14/08).
                { text: t('budget.nouveauBudget'), onPress: onNouveauBudget },
                { text: t('finances.nouvelleLigne'), onPress: onNouvelle },
                { text: t('commun.annuler'), style: 'cancel' },
              ]);
            } }}>
          {/* Symétrique du 🧾 de la vue mois : sans lui, on ne revenait qu'en
              ressortant de l'écran pour y re-entrer. Une bascule qui ne
              fonctionne que dans un sens n'est pas une bascule. */}
          {!!onModeMois && (
            <TouchableOpacity onPress={onModeMois} hitSlop={HIT}
              accessibilityLabel={t('finances.vueMois')}>
              <Text style={{ fontSize: 19, marginLeft: 14 }}>📅</Text>
            </TouchableOpacity>
          )}
          {/* La bascule de SYNTHÈSE (16/08) : grisée quand la carte est cachée. */}
          <TouchableOpacity onPress={basculerSynthese} hitSlop={HIT}
            accessibilityLabel={t('finances.basculerSynthese')}>
            <Text style={{ fontSize: 19, marginLeft: 14, opacity: syntheseVisible ? 1 : 0.35 }}>📊</Text>
          </TouchableOpacity>
        </PageHeader>

        <View style={s.entete}>
          {syntheseVisible && (
            <View style={s.totalCarte}>
              <Text style={s.totalLbl}>{t('finances.configBudget')}</Text>
              <Text style={s.totalVal}>{liste.length}</Text>
              <Text style={s.detail}>{t('finances.nouvelleLigne')}</Text>
            </View>
          )}

          {/* Le modèle commun : 🔽 filtre · champ · ↕ tri (15/08). */}
          <BarreRecherche valeur={recherche} onChange={setRecherche}
            nbFiltres={nbFiltres} onFiltres={() => setFeuille('filtres')}
            triActif={tri !== 'montant'} onTri={() => setFeuille('tri')} />
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={s.corps}>
          {liste.length === 0 ? (
            <EmptyState icon="📋" texte={t('finances.aucuneLigne')} />
          ) : (groupes || [{ cle: null, budget: null, lignes: liste }]).map(g => {
            // En vue globale, chaque budget est un accordéon ; dans une
            // enveloppe, il n’y a qu’un groupe et on le rend à plat.
            const plie = (replies ?? new Set((groupes || []).map(x => x.cle))).has(g.cle);
            const contenu = (plie && groupes) ? [] : g.lignes;
            return (
              <View key={String(g.cle)}>
                {!!groupes && (() => {
                  /* Grammaire commune (15/08) : le chevron PLIE, le corps
                     ouvre la FICHE du budget, le total nominal signé à
                     droite. La rangée « Sans budget » n'est pas une
                     enveloppe : ni fiche, ni swipe — elle plie, c'est tout. */
                  const entete = (
                    <EnTeteArbre plie={plie} onBasculer={() => basculer(g.cle)}
                      emoji={g.budget ? emojiBudget(g.budget) : '❓'}
                      titre={g.budget ? g.budget.nom : t('budget.sansBudget')}
                      nb={g.lignes.length}
                      onCorps={g.budget ? () => onFicheBudget?.(g.budget) : undefined}
                      droite={(
                        <Text style={[s.groupeTotal,
                          g.totalSigne > 0.005 && { color: UI.ok || '#2e7d32' },
                          g.totalSigne < -0.005 && { color: UI.danger || '#c0392b' }]}>
                          {g.totalSigne > 0 ? '+ ' : g.totalSigne < 0 ? '− ' : ''}
                          {formatEuro(Math.abs(g.totalSigne))}
                        </Text>
                      )} />
                  );
                  return g.budget ? (
                    <LigneSwipe espacement={0} enabled
                      onEditer={() => onEditerBudget?.(g.budget)}
                      onSupprimer={() => onSupprimerBudget?.(g.budget)}>
                      {entete}
                    </LigneSwipe>
                  ) : entete;
                })()}
                <View style={groupes ? { marginLeft: RETRAIT_ENFANTS } : null}>
                {contenu.map(o => {
          const l = o.ligne;
          return (
            <LigneSwipe key={l.id} espacement={8} enabled
              onEditer={() => onEditerLigne?.(l)}
              onSupprimer={() => confirmerSuppression(
                t('finances.actSupprimerLigne'), l.libelle || '', () => onSupprimerLigne?.(l))}>
              {/* Tap = fiche d'information, swipe → = éditer. Principe
                  app-wide ; c'est le quatrième écran où je l'avais manqué. */}
              <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => onFiche?.(l)}>
                <Text style={{ fontSize: 19 }}>{emojiCategorie(l.categorie)}</Text>
                {/* TROIS étages (Cédric, 16/08) : le nom, puis fréquence et
                    inflation, puis la période — chacun chez soi, rien ne
                    se dispute la place. */}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.libelle} numberOfLines={1}>
                    {l.libelle || t(cleCategorie(l.categorie))}
                  </Text>
                  <Text style={s.sous} numberOfLines={1}>
                    {t(`budget.freq_${l.frequence || 'mensuelle'}`)}
                    {'  ·  '}
                    {Number(l.inflationTaux)
                      ? t('finances.inflationCourte', { taux: l.inflationTaux })
                      : t('finances.sansInflation')}
                  </Text>
                  <Text style={s.sous} numberOfLines={1}>
                    {periode(l)}
                    {l.actif === false ? `  ·  ${t('finances.nd_inactive')}` : ''}
                  </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={[s.montant, l.sens === 'revenu' && { color: UI.ok || '#2e7d32' },
                    l.actif === false && { opacity: 0.45 }]}>
                    {montantOperation(l.montant, l.sens)}
                  </Text>
                  {/* La moyenne RELEVÉE en face du prévu : depuis le dernier
                      anniversaire d'inflation, sinon 12 mois. */}
                  {(moyennes.get(l.id)?.nb || 0) > 0 && (
                    <Text style={s.moy}>
                      {t('finances.moyCourte', { m: formatEuro(moyennes.get(l.id).montant) })}
                    </Text>
                  )}
                </View>
              </TouchableOpacity>
            </LigneSwipe>
          );
                })}
                </View>
              </View>
            );
          })}
        </ScrollView>

        <FeuilleFiltresCompte visible={!!feuille} ongletInitial={feuille || 'filtres'}
          sens={sens} tri={tri} tris={TRIS_LIGNES} avecHorsCalculVisible={false}
          onAppliquer={({ sens: se, tri: x }) => { setSens(se); setTri(x); }}
          onFermer={() => setFeuille(null)} />
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  entete: { paddingHorizontal: 12, paddingTop: 12, paddingBottom: 4, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  corps: { padding: 12, paddingBottom: 30 },
  totalCarte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 10 },
  totalLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  totalVal: { fontSize: 21, fontWeight: '800', color: UI.ink, marginTop: 2 },
  detail: { fontSize: 12, color: UI.muted, marginTop: 5, fontWeight: '700' },
  controles: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  btnIc: { width: 42, height: 42, borderRadius: 11, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  btnIcOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ic: { fontSize: 17 },
  pastille: { position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, borderRadius: 999, paddingHorizontal: 4, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  pastilleTxt: { fontSize: 10.5, color: '#fff', fontWeight: '800' },
  rechercheZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12 },
  recherche: { flex: 1, paddingVertical: 10, fontSize: 14, color: UI.ink },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12 },
  groupeTotal: { fontSize: 13.5, fontWeight: '800', color: UI.muted },
  libelle: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  montant: { fontSize: 15, fontWeight: '800', color: UI.ink },
  moy: { fontSize: 11.5, color: UI.muted, fontWeight: '700', marginTop: 2 },
});
